import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  UploadCloud, Loader2, Lock, ArrowRight, Sparkles,
  FileText, Eye, AlertTriangle, RefreshCw,
} from "lucide-react";

const REVEAL_PCT   = { anonymous: 25, free: 50, paid: 100 };
const NEXT_ACTION  = {
  anonymous: { label: "Log in to unlock 50%", to: "/login" },
  free:      { label: "Upgrade to Pro for 100% + download", to: "/pricing" },
  paid:      null,
};

const PLACEHOLDER_WIDTHS = ["w-full", "w-5/6", "w-4/5", "w-full", "w-3/4", "w-5/6"];

export default function DemoWidget() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep]       = useState("");
  const [err, setErr]         = useState("");
  const [expired, setExpired] = useState(false);
  const fileRef = useRef();

  const plan = user?.plan || "free";
  const revealLevel = !user ? "anonymous" : (plan === "pro" || plan === "business") ? "paid" : "free";

  // On mount — try to restore a cached result
  useEffect(() => {
    if (localStorage.getItem("demoScanned")) fetchCached();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When user logs in or upgrades — re-fetch to upgrade reveal level
  useEffect(() => {
    if (result) fetchCached();
  }, [user?.uid, user?.plan]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCached = async () => {
    try {
      const { data } = await api.get("/demo/result");
      setResult(data);
      setExpired(false);
    } catch (e) {
      if (e?.response?.status === 404) {
        localStorage.removeItem("demoScanned");
        setExpired(true);
        setResult(null);
      }
    }
  };

  const scan = async (file) => {
    if (!file) return;
    setLoading(true); setStep("Uploading…"); setErr(""); setResult(null);
    try {
      setStep("Detecting AI content & paraphrasing…");
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/demo/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      localStorage.setItem("demoScanned", "1");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Scan failed. Please try again.";
      if (e?.response?.status === 429) {
        // Hit rate limit — still return cached
        fetchCached();
      } else {
        setErr(detail);
      }
    } finally {
      setLoading(false); setStep("");
    }
  };

  const onPick = (e) => scan(e.target.files?.[0]);
  const onDrop = (e) => { e.preventDefault(); scan(e.dataTransfer.files?.[0]); };

  const revealed  = (result?.sentences || []).filter(s => !s.hidden);
  const hidden    = (result?.sentences || []).filter(s => s.hidden);
  const aiPct     = result?.aiPercentage ?? 0;
  const nextAction = NEXT_ACTION[result?.revealLevel || revealLevel];

  // ── No result yet ──────────────────────────────────────────────────────────
  if (!result) {
    return (
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs uppercase tracking-widest text-indigo-300 mb-4">
            <Sparkles size={11}/> Try it free — no account needed
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Drop your document. See it humanized.
          </h2>
          <p className="mt-3 text-zinc-400 text-sm max-w-xl mx-auto">
            Upload any PDF, DOCX, or TXT. We scan every sentence for AI, paraphrase the flagged ones, and show you the result — instantly, before you sign up.
          </p>
        </div>

        {expired && (
          <div className="mb-4 flex items-center gap-2 text-amber-300 text-sm glass rounded-xl px-4 py-3 border border-amber-500/20">
            <AlertTriangle size={14}/> Your free demo expired. <Link to="/register" className="underline ml-1">Create a free account</Link> for full access.
          </div>
        )}

        {err && (
          <div className="mb-4 flex items-center gap-2 text-red-400 text-sm glass rounded-xl px-4 py-3 border border-red-500/20">
            <AlertTriangle size={14}/>{err}
          </div>
        )}

        <div
          onDragOver={e => e.preventDefault()} onDrop={onDrop}
          onClick={() => !loading && fileRef.current?.click()}
          className="cursor-pointer rounded-3xl border-2 border-dashed border-white/[0.12] hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all p-12 text-center glass"
        >
          <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.pptx" onChange={onPick} hidden/>
          <UploadCloud size={40} className="mx-auto text-indigo-300"/>
          <div className="mt-4 text-lg font-semibold">
            {loading ? step : "Drop your document here"}
          </div>
          <div className="mt-1 text-sm text-zinc-400">
            {loading ? "This takes ~10 seconds" : "PDF, DOCX, PPTX or TXT — up to 500 words free"}
          </div>
          {loading && <Loader2 size={20} className="mx-auto mt-5 animate-spin text-indigo-400"/>}
          {!loading && (
            <div className="mt-5 inline-flex flex-wrap gap-2 justify-center">
              {["PDF","DOCX","PPTX","TXT"].map(t => (
                <span key={t} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded glass">{t}</span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-center gap-6 text-xs text-zinc-500">
          <span className="flex items-center gap-1.5"><Eye size={12}/>25% revealed free</span>
          <span>·</span>
          <span>50% after login</span>
          <span>·</span>
          <span>100% after upgrade</span>
        </div>
      </section>
    );
  }

  // ── Result view ────────────────────────────────────────────────────────────
  return (
    <section className="max-w-5xl mx-auto px-6 lg:px-12 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Free Demo Result</div>
          <h2 className="mt-1 text-2xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Your document, humanized.
          </h2>
        </div>
        <button
          onClick={() => { setResult(null); setErr(""); localStorage.removeItem("demoScanned"); }}
          className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white glass px-3 py-1.5 rounded-lg"
        >
          <RefreshCw size={12}/> New scan
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
        <div className="glass rounded-xl p-3 sm:p-4 text-center">
          <div className={`text-xl sm:text-3xl font-bold ${aiPct > 0 ? "text-red-400" : "text-emerald-400"}`}>{aiPct}%</div>
          <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-zinc-400 mt-1">AI Detected</div>
        </div>
        <div className="glass rounded-xl p-3 sm:p-4 text-center">
          <div className="text-xl sm:text-3xl font-bold text-indigo-300">{REVEAL_PCT[result.revealLevel]}%</div>
          <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-zinc-400 mt-1">Revealed</div>
        </div>
        <div className="glass rounded-xl p-3 sm:p-4 text-center">
          <div className="text-xl sm:text-3xl font-bold">{result.wordCount}</div>
          <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-zinc-400 mt-1">Words scanned</div>
        </div>
      </div>

      <div className="glass-strong rounded-3xl overflow-hidden">
        {/* Revealed sentences */}
        <div className="p-5 sm:p-6">
          <div className="flex items-center gap-2 text-xs text-zinc-400 uppercase tracking-widest mb-4">
            <Eye size={12}/> Showing {result.revealCount} of {result.totalSentences} sentences
          </div>
          <div className="space-y-2 text-sm leading-7">
            {revealed.map((s) => (
              <span
                key={s.idx}
                className={`inline ${s.isAI ? "sentence-ai" : "sentence-human"}`}
                title={s.isAI ? `AI score: ${s.score}% → paraphrased` : `Human score: ${100 - s.score}%`}
              >
                {s.isAI && s.paraphrased ? s.paraphrased : s.text}{" "}
              </span>
            ))}
          </div>
        </div>

        {/* Hidden section with blur + CTA overlay */}
        {hidden.length > 0 && (
          <div className="relative border-t border-white/[0.06]">
            {/* Blurred placeholder bars */}
            <div className="p-5 sm:p-6 select-none pointer-events-none" aria-hidden="true">
              {hidden.slice(0, 6).map((_, i) => (
                <div key={i} className="mb-3 space-y-1.5">
                  <div className={`h-3 rounded-full bg-white/[0.07] blur-[3px] ${PLACEHOLDER_WIDTHS[i % PLACEHOLDER_WIDTHS.length]}`}/>
                  {i % 2 === 0 && <div className="h-3 rounded-full bg-white/[0.05] blur-[3px] w-3/5"/>}
                </div>
              ))}
            </div>

            {/* Gradient fade + CTA */}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-b from-transparent via-background/70 to-background/95">
              <Lock size={28} className="text-indigo-400 mb-3"/>
              <div className="text-base font-semibold text-center px-4">
                {hidden.length} more sentences are hidden
              </div>
              <div className="text-xs text-zinc-400 mt-1 text-center px-6">
                {result.revealLevel === "anonymous"
                  ? "Log in for free to unlock 50% of the humanized result."
                  : "Upgrade to Pro to unlock 100% and download the full document."}
              </div>
              {nextAction && (
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <Link to={nextAction.to}>
                    <Button className="bg-indigo-600 hover:bg-indigo-700 glow-primary px-5">
                      {nextAction.label} <ArrowRight size={14} className="ml-1"/>
                    </Button>
                  </Link>
                  {result.revealLevel === "anonymous" && (
                    <Link to="/register">
                      <Button variant="outline" className="glass px-5">Create free account</Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Fully revealed CTA */}
        {hidden.length === 0 && (
          <div className="border-t border-white/[0.06] px-5 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-emerald-400 flex items-center gap-2">
              <Sparkles size={14}/> 100% revealed — go to your dashboard to download
            </div>
            <Button onClick={() => nav("/dashboard")} className="bg-indigo-600 hover:bg-indigo-700">
              Open Dashboard <ArrowRight size={14} className="ml-1"/>
            </Button>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-5 text-xs text-zinc-500">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500/50"/>AI → paraphrased</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/50"/>Human (unchanged)</span>
      </div>
    </section>
  );
}
